
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCallLogs } from '@/hooks/useCallLogs';
import { useAuth } from '@/context/AuthContext';
import { callLogsService } from '@/services/callLogsService';
import { useTwilioContext } from '@/context/TwilioContext';

export const useCallHistoryData = () => {
  const { user } = useAuth();
  const { callInProgress } = useTwilioContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOutcome, setFilterOutcome] = useState<string>('all');
  
  // Use authenticated user's ID, fallback to 2 to match the analytics API calls I see in network logs
  const userId = user?.id || 2;
  console.log('useCallHistoryData: Using userId:', userId);
  
  // Pass shouldFetch option to prevent updates during calls
  const { callLogs, loading, error, refetch } = useCallLogs(userId, {
    shouldFetch: !callInProgress
  });

  // Convert call logs to history format for display with proper memoization
  const callHistory = useMemo(() => {
    console.log('useCallHistoryData: Converting', callLogs.length, 'call logs to history format');
    const formatted = callLogs.map((log, index) => {
      console.log(`useCallHistoryData: Processing log ${index + 1}:`, log);
      return callLogsService.formatCallLogForHistory(log);
    });
    console.log('useCallHistoryData: Formatted', formatted.length, 'history items');
    return formatted;
  }, [callLogs]);

  const filteredHistory = useMemo(() => {
    const filtered = callHistory.filter(call => {
      const matchesSearch = searchQuery === '' || 
        call.number.includes(searchQuery) ||
        call.name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filterOutcome === 'all' || call.outcome === filterOutcome;
      
      return matchesSearch && matchesFilter;
    });
    
    // Sort by timestamp descending (newest first)
    const sorted = filtered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    console.log('useCallHistoryData: Filtered from', callHistory.length, 'to', sorted.length, 'items');
    return sorted;
  }, [callHistory, searchQuery, filterOutcome]);

  const exportHistory = useCallback(() => {
    console.log('useCallHistoryData: Exporting', filteredHistory.length, 'history items');
    const csvContent = [
      ['Date', 'Time', 'Number', 'Name', 'Direction', 'Duration', 'Outcome', 'Quality', 'Notes'],
      ...filteredHistory.map(call => [
        call.timestamp.toLocaleDateString(),
        call.timestamp.toLocaleTimeString(),
        call.number,
        call.name || '',
        call.direction,
        call.duration,
        call.outcome,
        call.quality || '',
        call.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filteredHistory]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('useCallHistoryData: State update - callLogs:', callLogs.length, 'loading:', loading, 'error:', error);
  }, [callLogs.length, loading, error]);

  return {
    callHistory: filteredHistory,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterOutcome,
    setFilterOutcome,
    exportHistory,
    refetch,
    totalCount: callHistory.length
  };
};
